<template>
  <section data-testid="balances">
    <BalancesLoader v-if="isLoading" />
    <BalancesError v-else-if="hasError" @reload="fetchBalances" />
    <div v-else-if="balances" class="balances">
      <HeadingDisplay
        title=""
        head="Saldo em conta"
        :subtitle="balances.accountAmount.updatedAt"
      >
        <template #title-component>
          <Money>{{ balances.accountAmount.amount.formatted }}</Money>
        </template>
      </HeadingDisplay>
      <div class="balances__heading">
        <BaseText size="p6">Reserva</BaseText>
        <div class="balances__value">
          <Heading :level="6">
            <Money>{{ balances.totalReservedAmount.amount.formatted }}</Money>
          </Heading>
          <ButtonIconContextual
            size="extra-small"
            label="Informações sobre total em reservas"
            icon="EF0080"
            @click="openDetailsModal(balances.totalReservedAmount.details)"
          />
        </div>
      </div>
      <div class="balances__heading">
        <BaseText size="p6">Saldo em trânsito</BaseText>
        <div class="balances__value">
          <Heading :level="6">
            <Money>{{ balances.totalTransitAmount.amount.formatted }}</Money>
          </Heading>
          <ButtonIconContextual
            size="extra-small"
            label="Informações sobre total em trânsito"
            icon="EF0080"
            @click="openDetailsModal(balances.totalTransitAmount.details)"
          />
        </div>
      </div>
      <div
        v-if="balances.blockedAmount.amount.value > 0"
        class="balances__heading"
      >
        <BaseText size="p6">Saldo bloqueado</BaseText>
        <div class="balances__value">
          <Heading :level="6">
            <Money>{{ balances.blockedAmount.amount.formatted }}</Money>
          </Heading>
          <ButtonIconContextual
            size="extra-small"
            label="Informações sobre saldo bloqueado"
            icon="EF0080"
            @click="openDetailsModal(balances.blockedAmount.details)"
          />
        </div>
      </div>
      <div
        v-if="balances.marginStock?.amount?.value > 0"
        class="balances__heading"
      >
        <BaseText size="p6">Saldo de garantia</BaseText>
        <div class="balances__value">
          <Heading :level="6">
            <Money>{{ balances.marginStock.amount.formatted }}</Money>
          </Heading>
          <ButtonIconContextual
            size="extra-small"
            label="Informações sobre saldo de garantia"
            icon="EF0080"
            @click="openDetailsModal(balances.marginStock.details)"
          />
        </div>
      </div>
    </div>

    <BalanceDetailsModal
      :is-open="isDetailsModalOpen"
      :details="detailsModal"
      @close="isDetailsModalOpen = false"
    />
  </section>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue';

import {
  BaseText,
  HeadingDisplay,
  Heading,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { BalancesLoader } from './components/BalancesLoader';
import { BalancesError } from './components/BalancesError';
import { Money } from '@/modules/common/components/Money';
import { BalanceDetailsModal } from './components/BalanceDetailsModal';

import {
  getBalances,
  IBalancesResponse,
  IDetails
} from '@/modules/transaction/services/unified-account';

const isLoading = ref(true);
const hasError = ref(false);
const balances = ref<IBalancesResponse | null>(null);

const isDetailsModalOpen = ref(false);
const detailsModal = ref({});

function openDetailsModal(details: IDetails) {
  detailsModal.value = details;
  isDetailsModalOpen.value = true;
}

async function fetchBalances() {
  hasError.value = false;
  isLoading.value = true;

  try {
    balances.value = await getBalances();
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

onBeforeMount(async () => {
  await fetchBalances();
});
</script>

<style lang="less" scoped>
.balances {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: @size-spacing-x900;

  .heading-display {
    gap: 0;
  }

  &__heading {
    flex: auto;
    max-width: 165px;

    .p6 {
      color: @element-primary !important;
    }
  }

  &__value {
    display: flex;
    align-items: center;
  }

  @media screen and (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
  }
}
</style>
